import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HREFLANG_BY_LOCALE,
  PUBLIC_LOCALES,
  buildLocalizedPath,
  getPathWithoutLocale,
  getPublicLocaleFromI18nLanguage,
} from '../utils/locale';

const SITE_URL = 'https://rmgconstruction.am';
const JSON_LD_ID = 'rmg-json-ld';

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function absoluteUrl(path) {
  return `${SITE_URL}${path}`;
}

function buildAlternateUrl(locale) {
  const path = buildLocalizedPath(locale, window.location.pathname, '');
  return absoluteUrl(path);
}

function buildJsonLd(locale, t) {
  const pageUrl = buildAlternateUrl(locale);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'RMG Construction LLC',
        alternateName: ['RMG Construction', 'ԷՌ ԷՄ ՋԻ ՔՆՍԹՐԱՔՇՆ'],
        url: SITE_URL,
        logo: `${SITE_URL}/og-image.jpg`,
        email: 'info@rmgconstruction.am',
        telephone: '+37443537070',
        sameAs: [
          'https://www.instagram.com/rmgconstruction.am/',
          'https://www.facebook.com/',
        ],
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#localbusiness`,
        name: t('seo.localBusinessName'),
        image: `${SITE_URL}/og-image.jpg`,
        url: SITE_URL,
        telephone: '+37443537070',
        email: 'info@rmgconstruction.am',
        address: {
          '@type': 'PostalAddress',
          streetAddress: t('contact.addressValue'),
          addressLocality: 'Yerevan',
          addressCountry: 'AM',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 40.214503,
          longitude: 44.481525,
        },
        openingHours: 'Mo-Fr 10:00-17:00',
        parentOrganization: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'RMG Construction',
        inLanguage: HREFLANG_BY_LOCALE[locale],
        publisher: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: t('seo.title'),
        description: t('seo.description'),
        inLanguage: HREFLANG_BY_LOCALE[locale],
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
      },
    ],
  };
}

function SeoManager() {
  const { t, i18n } = useTranslation();
  const locale = getPublicLocaleFromI18nLanguage(i18n.language);

  const seo = useMemo(
    () => ({
      title: t('seo.title'),
      description: t('seo.description'),
      keywords: t('seo.keywords'),
      ogTitle: t('seo.ogTitle'),
      ogDescription: t('seo.ogDescription'),
    }),
    [t]
  );

  useEffect(() => {
    const canonicalPath = buildLocalizedPath(locale, window.location.pathname, '');
    const canonicalUrl = absoluteUrl(canonicalPath);
    const alternatePathWithoutLocale = getPathWithoutLocale(window.location.pathname);

    document.documentElement.lang = HREFLANG_BY_LOCALE[locale];
    document.title = seo.title;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: seo.description,
    });

    upsertMeta('meta[name="keywords"]', {
      name: 'keywords',
      content: seo.keywords,
    });

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow, max-image-preview:large',
    });

    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: seo.ogTitle,
    });

    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: seo.ogDescription,
    });

    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });

    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl,
    });

    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: `${SITE_URL}/og-image.jpg`,
    });

    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: locale === 'hy' ? 'hy_AM' : locale,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: seo.ogTitle,
    });

    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: seo.ogDescription,
    });

    upsertLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: canonicalUrl,
    });

    PUBLIC_LOCALES.forEach((publicLocale) => {
      upsertLink(`link[rel="alternate"][data-locale="${publicLocale}"]`, {
        rel: 'alternate',
        hrefLang: HREFLANG_BY_LOCALE[publicLocale],
        href: `${SITE_URL}/${publicLocale}${alternatePathWithoutLocale}`,
        'data-locale': publicLocale,
      });
    });

    upsertLink('link[rel="alternate"][data-locale="x-default"]', {
      rel: 'alternate',
      hrefLang: 'x-default',
      href: `${SITE_URL}/hy${alternatePathWithoutLocale}`,
      'data-locale': 'x-default',
    });

    let jsonLd = document.getElementById(JSON_LD_ID);

    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = JSON_LD_ID;
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }

    jsonLd.textContent = JSON.stringify(buildJsonLd(locale, t));
  }, [locale, seo, t]);

  return null;
}

export default SeoManager;
